/* Quick add: prepare the inert product HTML before Dawn mounts it.
 * Moving live variant-selects would register its change handler a second time.
 * Keep Dawn's fetch, variant updates and cart submission unchanged.
 *
 * Tamanho começa sem seleção, mesmo gate que a PDP real ganhou em
 * assets/ws-size-gate.js/snippets/ws-size-gate.liquid (07/09/2026) — botão
 * aria-disabled (não disabled, mantém no tab order) + texto "Select size" +
 * marcador data-ws-size-gated, com o clique que leva aos tamanhos delegado
 * no document (ver o listener no fim do arquivo: o nó preparado aqui é
 * serializado pelo quick-add.js e nunca chega ao DOM, então nada que se
 * prenda nele sobrevive). O spinner-ao-trocar-de-tamanho e a reversão do
 * aria-disabled/texto no PUB_SUB_EVENTS.variantChange são cobertos por
 * assets/ws-size-gate.js — esse arquivo hoje trata qualquer product-info da
 * página, dentro ou fora de quick-add-modal, então não precisa de código
 * duplicado aqui.
 */
(function () {
  // Multiple collection sections can include this shared asset.
  if (window.wsQuickAddInitialized) return;
  window.wsQuickAddInitialized = true;

  const preparedModals = new WeakSet();

  function prepareProduct(product) {
    if (!product.matches('product-info')) return;
    const info = product.querySelector('.product__info-container');
    const picker = info?.querySelector('variant-selects');
    const buyButtons = info?.querySelector('product-form')?.parentElement;
    if (!picker || !buyButtons || info.querySelector('.ws-quick-add__summary')) return;

    const summary = product.ownerDocument.createElement('div');
    summary.className = 'ws-quick-add__summary';
    const details = product.ownerDocument.createElement('div');
    details.className = 'ws-quick-add__details';
    const media = product.querySelector('.product__media-wrapper');
    if (media) summary.append(media);
    for (const selector of ['.product__title', '[id^="price-"]', '.product__view-details']) {
      const node = info.querySelector(selector);
      if (node) details.append(node);
    }
    summary.append(details);
    const actions = product.ownerDocument.createElement('div');
    actions.className = 'ws-quick-add__actions';
    actions.append(picker, buyButtons);
    info.prepend(summary, actions);

    // Only size starts empty; preserve any other option used to resolve
    // variants. data-option-name, não <legend>/texto (mesmo motivo do fix
    // em ws-size-gate.liquid: só funciona com picker_type "button"; com
    // dropdown/swatch, o gate desligaria sozinho, sem erro).
    const sizeRadios = Array.from(picker.querySelectorAll('input[type="radio"][data-option-name]')).filter(
      (input) => input.dataset.optionName.trim().toLowerCase() === 'size'
    );
    // Só 1 valor (ex. "One Size") não tem nada pra escolher — gatear aqui
    // só forçaria um clique sem sentido, sem prevenir erro nenhum.
    if (sizeRadios.length < 2) return;

    sizeRadios.forEach((input) => {
      input.removeAttribute('checked');
      input.checked = false;
      input.required = true;
    });

    buyButtons.querySelector('[name="id"]').value = '';

    const submitButton = buyButtons.querySelector('[type="submit"]');
    if (!submitButton) return;

    const textEl = submitButton.querySelector('span');
    if (textEl) {
      submitButton.dataset.wsOriginalText = textEl.textContent;
      textEl.textContent = 'Select size';
    }
    submitButton.setAttribute('aria-disabled', 'true');
    // Mesmo marcador do gate da PDP (snippets/ws-size-gate.liquid): dá a cor
    // cheia ao botão e ativa o clique que leva aos tamanhos (listener no
    // fim deste arquivo). Por ser atributo, sobrevive à serialização que o
    // quick-add.js faz deste nó; sai em assets/ws-size-gate.js quando a
    // variante é confirmada.
    submitButton.setAttribute('data-ws-size-gated', '');
  }

  // Clique (ou Enter) em "Select size" dentro do quick add leva aos
  // tamanhos, como na PDP (25/09/2026): foca o primeiro tamanho disponível
  // e destaca os chips em marrom por 1,6s. O CSS do destaque e da cor cheia
  // do botão vem do ws-size-gate.liquid, que chega junto com o HTML da PDP
  // que a gaveta renderiza.
  //
  // Delegado no document, não preso ao product-info: quick-add.js insere a
  // gaveta com HTMLUpdateUtility.setInnerHTML(..., productElement.outerHTML),
  // ou seja, a partir de uma string, e o nó preparado em prepareProduct
  // nunca chega ao DOM. Um listener registrado nele se perdia (era o caso do
  // guard que existia aqui até 25/09/2026; o submit já não acontecia porque
  // o próprio product-form.js ignora botão com aria-disabled).
  const attentionTimers = new WeakMap();
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('quick-add-modal .product-form__submit[data-ws-size-gated]');
      if (!button || button.getAttribute('aria-disabled') !== 'true') return;
      event.preventDefault();
      event.stopPropagation();

      const product = button.closest('product-info');
      if (!product) return;
      const sizes = Array.from(product.querySelectorAll('variant-selects input[type="radio"][data-option-name]')).filter(
        (input) => input.dataset.optionName.trim().toLowerCase() === 'size'
      );
      const target = sizes.find((input) => !input.classList.contains('disabled')) || sizes[0];
      if (!target) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.closest('variant-selects')?.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
      target.focus({ preventScroll: true });

      product.classList.remove('ws-size-attention');
      void product.offsetWidth;
      product.classList.add('ws-size-attention');
      clearTimeout(attentionTimers.get(product));
      attentionTimers.set(
        product,
        setTimeout(() => product.classList.remove('ws-size-attention'), 1600)
      );
    },
    true
  );

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('modal-opener');
    const modal = opener && document.querySelector(opener.dataset.modal);
    if (!modal?.classList.contains('quick-add-modal--compact') || preparedModals.has(modal)) return;
    preparedModals.add(modal);

    const content = modal.querySelector('.quick-add-modal__content');
    content.classList.add('drawer__inner', 'motion-reduce');
    const heading = document.createElement('h2');
    heading.className = 'ws-quick-add__heading';
    heading.textContent = opener.querySelector('button > span')?.textContent.trim();
    content.prepend(heading);

    // Reuse the cart drawer close pattern and the SVG already rendered by Liquid.
    const close = modal.querySelector('.quick-add-modal__toggle');
    close.classList.add('drawer__close');
    const icon = close.querySelector('svg');
    const wrapper = document.createElement('span');
    wrapper.className = 'svg-wrapper';
    wrapper.append(icon);
    close.append(wrapper);

    const preprocess = modal.preprocessHTML.bind(modal);
    modal.preprocessHTML = (product) => {
      preprocess(product);
      prepareProduct(product);
    };
  }, true);
})();
